// Intent: "Ustawienia gospodarstwa" used to dead-end at /soon. Real settings:
// household name, monthly budget, situation declarations (feed the protection
// score) and the member list with removal (owner is untouchable).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useBudgetStore } from '@/store/budget-store';
import { useHouseholdStore } from '@/store/household-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { HouseholdDeclarations } from '@/types/domain';

const DECLS: { key: keyof HouseholdDeclarations; labelKey: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { key: 'ownsHome', labelKey: 'onboarding.situationHome', icon: 'home-outline' },
  { key: 'hasMortgage', labelKey: 'onboarding.situationMortgage', icon: 'business-outline' },
  { key: 'hasCar', labelKey: 'onboarding.situationCar', icon: 'car-outline' },
  { key: 'hasKids', labelKey: 'onboarding.situationKids', icon: 'happy-outline' },
  { key: 'runsBusiness', labelKey: 'onboarding.situationBusiness', icon: 'briefcase-outline' },
];

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

export default function HouseholdSettingsScreen() {
  const { t } = useTranslation();
  const household = useHouseholdStore((s) => s.household);
  const renameHousehold = useHouseholdStore((s) => s.renameHousehold);
  const setDeclarations = useHouseholdStore((s) => s.setDeclarations);
  const removeMember = useHouseholdStore((s) => s.removeMember);
  const monthlyBudgetGr = useBudgetStore((s) => s.monthlyBudgetGr);
  const setMonthlyBudget = useBudgetStore((s) => s.setMonthlyBudget);

  const [name, setName] = useState(household?.name ?? '');
  const [budgetRaw, setBudgetRaw] = useState(monthlyBudgetGr > 0 ? String(Math.round(monthlyBudgetGr / 100)) : '');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!household) return null;

  const onSave = async () => {
    await renameHousehold(name);
    const budgetGr = parseZl(budgetRaw);
    if (budgetGr !== monthlyBudgetGr) await setMonthlyBudget(budgetGr);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const toggleDecl = (key: keyof HouseholdDeclarations) => {
    void setDeclarations({ ...household.declarations, [key]: !household.declarations[key] });
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        >
          <ScreenHeader title={t('householdSettings.title')} />

          <TextField label={t('householdSettings.nameLabel')} value={name} onChangeText={setName} />
          <TextField
            label={t('householdSettings.budgetLabel')}
            placeholder="np. 6 000"
            keyboardType="numeric"
            value={budgetRaw}
            onChangeText={setBudgetRaw}
          />

          <PrimaryButton
            label={saved ? t('householdSettings.saved') : t('householdSettings.save')}
            disabled={!name.trim()}
            onPress={onSave}
          />

          {/* Situation declarations (protection-score inputs) */}
          <AppText variant="headlineSm">{t('householdSettings.situation')}</AppText>
          <Card style={{ padding: 0 }}>
            {DECLS.map(({ key, labelKey, icon }, idx) => {
              const active = household.declarations[key];
              return (
                <Pressable
                  key={key}
                  accessibilityRole="button"
                  onPress={() => toggleDecl(key)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: spacing.md,
                    minHeight: 56,
                    borderTopWidth: idx === 0 ? 0 : 1,
                    borderTopColor: colors.divider,
                  }}
                >
                  <Ionicons name={icon} size={22} color={colors.navy} />
                  <AppText variant="bodyLg" style={{ flex: 1 }}>
                    {t(labelKey)}
                  </AppText>
                  <Ionicons
                    name={active ? 'checkmark-circle' : 'ellipse-outline'}
                    size={24}
                    color={active ? colors.primary : colors.outlineVariant}
                  />
                </Pressable>
              );
            })}
          </Card>

          {/* Members with removal (owner protected) */}
          <AppText variant="headlineSm">{t('householdSettings.members')}</AppText>
          <Card style={{ padding: 0 }}>
            {household.members.map((member, idx) => (
              <View
                key={member.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: spacing.md,
                  borderTopWidth: idx === 0 ? 0 : 1,
                  borderTopColor: colors.divider,
                }}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: radius.full,
                    backgroundColor: member.role === 'OWNER' ? colors.navy : colors.primaryTile,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppText variant="labelLg" color={member.role === 'OWNER' ? '#FFFFFF' : colors.navy}>
                    {member.firstName.charAt(0)}
                  </AppText>
                </View>
                <View style={{ flex: 1 }}>
                  <AppText variant="labelLg">{member.firstName}</AppText>
                  {member.phone ? <AppText variant="bodyMd">{member.phone}</AppText> : null}
                </View>
                {member.role !== 'OWNER' && (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      if (confirmRemoveId === member.id) {
                        void removeMember(member.id);
                        setConfirmRemoveId(null);
                      } else {
                        setConfirmRemoveId(member.id);
                      }
                    }}
                    style={{ minHeight: 40, justifyContent: 'center' }}
                  >
                    <AppText variant="labelMd" color={colors.error}>
                      {confirmRemoveId === member.id ? t('householdSettings.removeConfirm') : t('householdSettings.remove')}
                    </AppText>
                  </Pressable>
                )}
              </View>
            ))}
          </Card>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
